import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
  lazy,
  Suspense,
  type ChangeEvent
} from "react";
import type { Editor } from "@tiptap/react";
import {
  IconButton,
  IconButtonVariant,
  IconButtonSize,
  InputSize
} from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  SentIcon,
  Alert02Icon,
  AttachmentIcon,
  PlusSignIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { AlertCircleIcon } from "@hugeicons-pro/core-solid-rounded";
import { useQueryClient } from "@tanstack/react-query";
import { Show } from "shared/components";
import { NoSubjectWarningModal } from "./NoSubjectWarningModal";
import { ConfirmDialog } from "shared/components/modals/ConfirmDialog";
import {
  createInboxVariableExtension,
  buildLinkChipHtml,
  docHasUnresolvedLinkChip,
  flattenVariableChips,
  resolveUnresolvedLinkChips,
  type LinkType
} from "./link-variable-utils";
import { InboxVariableDataContext } from "./link-variable-shared";
import { useInboxEmbed } from "../context/useInboxEmbed";
import { cx } from "shared/utils/classnames";
import { InboxInsertVariableMenu } from "./InboxInsertVariableMenu";
import { TemplatePickerButton } from "./TemplatePickerButton";
import {
  PendingAttachmentPreview,
  type PendingAttachment
} from "./PendingAttachmentPreview";
import { markAttachmentUploaded, dropAttachment } from "./pending-attachment";
import {
  useSendEmail,
  useSendSms,
  useSendChat,
  useUploadChatAttachment
} from "../api/hooks";
import { inboxKeys } from "../api/hooks";
import {
  useContactUnitLinks,
  useContactVariableValues,
  singleLeadForUnits
} from "../api/crm";
import type { ContactUnitLinks } from "../api/crm";
import { collectDocUnitIds } from "./link-variable-utils";
import {
  useSmsVariables,
  useEmailVariables
} from "../../settings-communication/api/hooks";
import { useEnsureConversation } from "../hooks/useEnsureConversation";
import { useInboxContext } from "../context/useInboxContext";
import { toast } from "react-toastify";
import { EditorRefApi } from "shared/components/Editor";
import { LiteTextEditor } from "shared/app/Editor/LiteTextEditor";
import { isTextEmpty } from "shared/app/ActivityLog/helpers";
import { getSmsLength, isOverSmsLimit, MAX_SMS_LENGTH } from "utils/sms-length";
import type { ComposerTab } from "./constants";
import type { EmailTemplate } from "../../settings-communication/api/types";

const SmsTemplateSelect = lazy(() =>
  import("./SmsTemplateSelect").then((m) => ({
    default: m.SmsTemplateSelect
  }))
);

const MAX_CHAT_ATTACHMENTS = 5;

// CRM-1288: SMS/MMS is image-only, one file, hard-capped at 5 MB — carriers drop
// oversized or non-image media. Twilio resizes images below this ceiling.
const MAX_SMS_ATTACHMENTS = 1;
const MAX_SMS_ATTACHMENT_BYTES = 5 * 1024 * 1024;
// Mirror communication-service's carrier-reliable allowlist exactly, so a file
// that passes here can't be rejected server-side after upload. `image/*` would
// admit SVG/ICO/HEIC that the server's magic-byte check drops.
const SMS_ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp"
];

interface ComposerDraft {
  html: string;
  text: string;
  subject: string;
}

// Unsent composer content, keyed by destination (conversation/contact +
// channel + reply target). Module-level so a draft survives composer
// unmounts within the session. Switching recipient or channel must never
// leak content between destinations — the composer is cleared on switch and
// the draft restored only when the user returns to where they started it.
const composerDrafts = new Map<string, ComposerDraft>();

const EMPTY_DRAFT: ComposerDraft = { html: "", text: "", subject: "" };

const SmsLengthNote = () => (
  <span className="flex items-center gap-1 text-danger-500">
    <HugeiconsIcon icon={AlertCircleIcon} size={14} />
    {`This message is too long to send — it must be under ${MAX_SMS_LENGTH} characters`}
  </span>
);

// Extracted to module scope to keep the component's cognitive complexity down.
const computeSendDisabled = (opts: {
  isChat: boolean;
  isSms: boolean;
  isReply: boolean;
  isEmpty: boolean;
  isPending: boolean;
  isUploading: boolean;
  pendingCount: number;
  applicationId?: number;
  phoneNumberId: string;
  toPhone: string;
  toEmail: string;
  mailboxId: string;
  isToOptedOut: boolean;
  hasUnresolvedLink: boolean;
}): boolean => {
  if (opts.isChat)
    return (
      (opts.isEmpty && opts.pendingCount === 0) ||
      opts.applicationId === undefined ||
      opts.isPending ||
      opts.isUploading
    );
  if (opts.isSms)
    return (
      (opts.isEmpty && opts.pendingCount === 0) ||
      !opts.phoneNumberId ||
      !opts.toPhone ||
      opts.isToOptedOut ||
      opts.isPending ||
      opts.isUploading ||
      opts.hasUnresolvedLink
    );
  if (opts.isReply)
    return (
      opts.isEmpty ||
      !opts.mailboxId ||
      opts.isPending ||
      opts.hasUnresolvedLink
    );
  return (
    opts.isEmpty ||
    !opts.mailboxId ||
    !opts.toEmail ||
    opts.isPending ||
    opts.hasUnresolvedLink
  );
};

const errorStatus = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } })?.response?.status;

// The server's own error message from an axios-style 4xx rejection, so we can
// surface the specific attachment reason (not an image / too large / not
// MMS-capable) instead of a generic toast. Undefined on a transport failure.
const attachmentErrorMessage = (err: unknown): string | undefined => {
  const response = (err as { response?: { data?: { message?: unknown } } })
    ?.response;
  if (!response) return undefined;
  const message = response.data?.message;
  if (typeof message === "string") return message;
  if (Array.isArray(message)) return message.join(", ");
  return "Failed to send. Please try again.";
};

interface ComposerFormProps {
  channel: ComposerTab;
  conversationId?: string;
  draftContactId?: number;
  mailboxId?: string;
  toEmail?: string;
  toName?: string;
  phoneNumberId?: string;
  toPhone?: string;
  applicationId?: number;
  recipientApplicantId?: number;
  isToOptedOut?: boolean;
  subject?: string;
  replyToMessageId?: string;
  contactId?: number;
  onMessageSent?: () => void;
  onTemplateSelect?: (template: EmailTemplate) => void;
}

/* eslint-disable sonarjs/cognitive-complexity -- unified composer: chat + sms + email + variable insertion + templates + opt-out */
export const ComposerForm = ({
  channel,
  conversationId,
  draftContactId,
  mailboxId = "",
  toEmail = "",
  toName,
  phoneNumberId = "",
  toPhone = "",
  applicationId,
  recipientApplicantId,
  isToOptedOut = false,
  subject: initialSubject = "",
  replyToMessageId,
  contactId,
  onMessageSent,
  onTemplateSelect
}: ComposerFormProps) => {
  const queryClient = useQueryClient();
  const {
    actions: { selectConversation }
  } = useInboxContext();
  const ensureConversation = useEnsureConversation(
    conversationId,
    draftContactId
  );
  const editorRef = useRef<EditorRefApi>(null);
  const editorInstanceRef = useRef<Editor | null>(null);
  const [editorReady, setEditorReady] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [smsText, setSmsText] = useState("");
  const [hasUnresolvedLink, setHasUnresolvedLink] = useState(false);
  const [subject, setSubject] = useState(initialSubject);
  const [showNoSubjectModal, setShowNoSubjectModal] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState<{
    plainText: string;
    html: string;
  } | null>(null);
  const sendEmailMutation = useSendEmail();
  const sendSmsMutation = useSendSms();
  const sendChatMutation = useSendChat();
  const uploadAttachment = useUploadChatAttachment();
  // Inside the message pop-up the composer follows the design's compact
  // card (node 2039:32114): a bordered input box with only attach, the
  // editor and a 34px send button.
  const embed = useInboxEmbed();
  const isCompact = Boolean(embed?.compact);
  const controlSize = isCompact ? IconButtonSize.XS : IconButtonSize.SM;
  const chatFileInputRef = useRef<HTMLInputElement>(null);
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingAttachment[]
  >([]);
  // Mirror of pendingAttachments read after awaits in the send path, so a
  // remove-while-sending is honored rather than sending the snapshot taken
  // before `ensureConversation()` resolved.
  // Assigned during render (like draftKeyRef) so a send reads the latest staged
  // list right after an await, without an effect lagging a render behind.
  const pendingAttachmentsRef = useRef<PendingAttachment[]>([]);
  pendingAttachmentsRef.current = pendingAttachments;
  // Synchronous in-flight guard: `isPending` only flips once mutateAsync starts,
  // so on the draft-conversation flow two fast clicks would both reach the send.
  const smsSendingRef = useRef(false);
  const isUploading = pendingAttachments.some((a) => a.status === "uploading");

  const isSms = channel === "sms";
  const isChat = channel === "chat";
  const isEmail = channel === "email";
  const isReply = !!replyToMessageId;
  const isPending =
    sendChatMutation.isPending ||
    sendSmsMutation.isPending ||
    sendEmailMutation.isPending;

  const contactIdStr = contactId !== undefined ? String(contactId) : undefined;
  // Same variable keys for both channels; pick the channel-appropriate source
  // so the insert menu offers the full lead variable list for email too.
  const { data: smsVariables = [] } = useSmsVariables("lead");
  const { data: emailVariables = [] } = useEmailVariables("lead");
  const variables = isSms ? smsVariables : emailVariables;

  // Units + resolved name-variable values are needed for BOTH channels now:
  // link chips resolve to a unit short URL, name chips to a resolved value.
  // Recipient of the message — used so name-variable previews (and the SMS
  // template render) resolve against the lead matching this recipient rather
  // than the contact's newest lead.
  const recipientAddress = isSms ? toPhone : toEmail;
  const { data: units = [] } = useContactUnitLinks(contactIdStr);
  const { data: variableValues = {} } = useContactVariableValues(
    contactIdStr,
    "lead",
    { address: recipientAddress || undefined }
  );

  // The contact's single unit, used to pre-select a link when there is no
  // ambiguity (exactly one unit across all the contact's leads).
  const singleUnit = useMemo(
    () => (units.length === 1 ? units[0] : null),
    [units]
  );

  // disableMentions removes the default @user mention plugin; this custom
  // extension renders variable chips (name chips display the resolved value,
  // link chips with a unit submenu) — no @-popup trigger. The extension is
  // static: chips get variables/units via InboxVariableDataContext below,
  // because the editor instantiates extensions once and would freeze any data
  // baked into their options at mount time (before the queries resolve).
  const variableExtension = useMemo(() => [createInboxVariableExtension()], []);

  const variableData = useMemo(
    () => ({ variables, units }),
    [variables, units]
  );

  // One draft per destination: an existing conversation, or a not-yet-created
  // draft conversation with a contact, further split by channel and reply
  // target so an SMS draft never shows up in the email composer (and vice
  // versa).
  const destinationKey = conversationId ?? `draft:${draftContactId ?? "none"}`;
  const draftKey = `${destinationKey}:${channel}:${replyToMessageId ?? ""}`;
  const draftKeyRef = useRef(draftKey);
  draftKeyRef.current = draftKey;

  const persistDraft = useCallback(
    (patch: Partial<ComposerDraft>) => {
      const next = {
        ...(composerDrafts.get(draftKey) ?? EMPTY_DRAFT),
        ...patch
      };
      if (!next.text.trim() && !next.subject.trim())
        composerDrafts.delete(draftKey);
      else composerDrafts.set(draftKey, next);
    },
    [draftKey]
  );

  const handleSubjectChange = useCallback(
    (value: string) => {
      setSubject(value);
      persistDraft({ subject: value });
    },
    [persistDraft]
  );

  // Clear/restore the composer whenever the destination changes: content
  // typed for one recipient/channel must never appear under another, but it
  // comes back when the user returns to where they started writing it.
  useEffect(() => {
    if (!editorReady) return;
    const stored = composerDrafts.get(draftKey);
    editorRef.current?.setEditorValue(stored?.html ?? "");
    setSubject(stored?.subject ?? initialSubject);
    const doc = editorRef.current?.getDocument();
    const text = doc?.text ?? "";
    setIsEmpty(isTextEmpty(text));
    setSmsText(isSms ? text : "");
    setHasUnresolvedLink(docHasUnresolvedLinkChip(doc?.json));
    // Staged attachments belong to the destination they were added on — reset
    // them on any conversation/channel switch, exactly like the editor content.
    // Without this a photo staged for lead A (or on Chat) leaks into the next
    // recipient/channel and gets sent to the wrong person or rejected server-side.
    setPendingAttachments((prev) => {
      prev.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl));
      return [];
    });
  }, [draftKey, editorReady, initialSubject, isSms]);

  const handleEditorChange = useCallback(() => {
    const doc = editorRef.current?.getDocument();
    if (!doc) return;

    setIsEmpty(isTextEmpty(doc.text));
    // Unresolved-link gating applies to BOTH channels: an unresolved link chip
    // serialises to an empty title, so it must block send for email too.
    setHasUnresolvedLink(docHasUnresolvedLinkChip(doc.json));
    if (isSms) setSmsText(doc.text);
    persistDraft({ html: doc.html, text: doc.text });
  }, [isSms, persistDraft]);

  const handleInsertName = useCallback((key: string, value: string) => {
    const editor = editorInstanceRef.current;
    if (!editor) return;
    // `title` is what the chip serialises to (renderText). Storing the
    // resolved value makes the outgoing SMS plaintext / email body carry
    // literal text rather than the variable token.
    editor
      .chain()
      .focus()
      .insertContent({
        type: "mention",
        attrs: {
          id: key,
          entity_identifier: key,
          entity_name: "partner_user",
          title: value
        }
      })
      .insertContent(" ")
      .run();
  }, []);

  const handleInsertLink = useCallback(
    (key: LinkType, unit: ContactUnitLinks) => {
      const editor = editorInstanceRef.current;
      if (!editor) return;
      editor
        .chain()
        .focus()
        .insertContent(buildLinkChipHtml(key, unit))
        .insertContent(" ")
        .run();
    },
    []
  );

  const finalizeDraftIfNeeded = (resolvedConversationId: string) => {
    if (draftContactId !== undefined && !conversationId) {
      queryClient.invalidateQueries({ queryKey: inboxKeys.conversations() });
      selectConversation(resolvedConversationId);
    }
  };

  // The lead this message is about, derived from the units the inserted link
  // chips point at. When all resolved links belong to one lead we send it so
  // the server scopes variables + activity to that lead; otherwise we omit it
  // and the server matches by recipient address (then newest lead).
  const leadIdForDoc = useCallback(
    (json: unknown): number | undefined =>
      singleLeadForUnits(
        collectDocUnitIds(json as Parameters<typeof collectDocUnitIds>[0]).map(
          (unitId) => units.find((unit) => unit.unit_id === unitId)
        )
      ),
    [units]
  );

  // Inline email body is already fully resolved FE-side (chips serialise to
  // literal text), and its activity is lead-scoped by communication-service via
  // the recipient address, so it needs no contact_id/lead_id on the send (which
  // would trigger the boompay-api endpoint's server-side resolution + its
  // contact_has_no_lead 422). Name-variable previews are lead-correct because
  // useContactVariableValues below is passed the recipient address.
  const doSendEmail = async (doc: { text: string; html: string }) => {
    try {
      const resolvedConversationId = await ensureConversation();
      await sendEmailMutation.mutateAsync({
        conversation_id: resolvedConversationId,
        content_plain: doc.text,
        content_html: flattenVariableChips(doc.html),
        mailbox_id: mailboxId,
        to_address: toEmail,
        to_name: toName,
        ...(isReply
          ? { reply_to_message_id: replyToMessageId }
          : { subject: subject || "(No subject)" })
      });
      editorRef.current?.clearEditor();
      setIsEmpty(true);
      composerDrafts.delete(draftKey);
      finalizeDraftIfNeeded(resolvedConversationId);
      onMessageSent?.();
    } catch {
      toast.error("Failed to send email");
    }
  };

  const doSendSms = async (doc: { text: string; json?: unknown }) => {
    if (smsSendingRef.current) return;
    // Pre-flight BEFORE ensureConversation so a bail-out never leaves a freshly
    // created, message-less draft conversation behind.
    const snapshot = pendingAttachmentsRef.current;
    if (snapshot.some((a) => a.status === "uploading")) {
      toast.error("Wait for the image to finish uploading");
      return;
    }
    if (!snapshot.some((a) => !!a.id) && isTextEmpty(doc.text)) {
      toast.error("Nothing to send — the image was removed");
      return;
    }
    smsSendingRef.current = true;
    const draftKeyAtSend = draftKeyRef.current;
    let attachmentsSnapshot: PendingAttachment[] = snapshot;
    let attachmentIds: string[] = [];
    try {
      const resolvedConversationId = await ensureConversation();
      // If the composer switched destination while the conversation was being
      // created, abort — never send (or attach) to a different recipient.
      if (draftKeyRef.current !== draftKeyAtSend) return;
      // Re-read after the await so an image removed during conversation creation
      // isn't sent.
      attachmentsSnapshot = pendingAttachmentsRef.current;
      attachmentIds = attachmentsSnapshot
        .map((a) => a.id)
        .filter((id): id is string => !!id);
      await sendSmsMutation.mutateAsync({
        conversation_id: resolvedConversationId,
        to_number: toPhone,
        content: doc.text,
        phone_number_id: phoneNumberId,
        lead_id: leadIdForDoc(doc.json),
        attachment_ids: attachmentIds.length ? attachmentIds : undefined
      });
      editorRef.current?.clearEditor();
      setIsEmpty(true);
      setSmsText("");
      clearStagedAttachments(attachmentsSnapshot);
      composerDrafts.delete(draftKey);
      finalizeDraftIfNeeded(resolvedConversationId);
      onMessageSent?.();
    } catch (err) {
      // Keep the staged image on every failure: communication-service releases
      // the attachment link on any error that doesn't send (validation, a
      // pre-create S3 blip, or a failed enqueue), so a retry reuses it. Surface
      // the server's specific reason on a 400.
      const serverMessage =
        errorStatus(err) === 400 ? attachmentErrorMessage(err) : undefined;
      toast.error(
        serverMessage ??
          (attachmentIds.length
            ? "Failed to send. Please try again."
            : "Failed to send SMS")
      );
    } finally {
      smsSendingRef.current = false;
    }
  };

  const doSendChat = async (doc: { text: string; html: string }) => {
    if (applicationId === undefined) return;
    const attachmentsSnapshot = pendingAttachments;
    const attachmentIds = attachmentsSnapshot
      .map((a) => a.id)
      .filter((id): id is string => !!id);
    editorRef.current?.clearEditor();
    setIsEmpty(true);
    setPendingAttachments([]);
    composerDrafts.delete(draftKey);
    try {
      const resolvedConversationId = await ensureConversation();
      await sendChatMutation.mutateAsync({
        conversation_id: resolvedConversationId,
        application_id: applicationId,
        content: doc.text,
        client_message_id: crypto.randomUUID(),
        attachment_ids: attachmentIds.length ? attachmentIds : undefined,
        // Deliver the chat-to-SMS mirror to the recipient the agent picked.
        recipient_applicant_id: recipientApplicantId
      });
      attachmentsSnapshot.forEach(
        (a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl)
      );
      finalizeDraftIfNeeded(resolvedConversationId);
      onMessageSent?.();
    } catch {
      persistDraft({ html: doc.html, text: doc.text });
      const currentText = editorRef.current?.getDocument()?.text ?? "";
      if (draftKeyRef.current === draftKey && isTextEmpty(currentText)) {
        editorRef.current?.setEditorValue(doc.html);
        setIsEmpty(isTextEmpty(doc.text));
        setPendingAttachments((prev) =>
          prev.length ? prev : attachmentsSnapshot
        );
      }
      toast.error("Failed to send chat message");
    }
  };

  const uploadChatFile = (file: File) => {
    const key = crypto.randomUUID();
    const isImage = file.type.startsWith("image/");
    const previewUrl = isImage ? URL.createObjectURL(file) : undefined;
    setPendingAttachments((prev) => [
      ...prev,
      { key, filename: file.name, isImage, previewUrl, status: "uploading" }
    ]);
    uploadAttachment.mutate(file, {
      onSuccess: (att) =>
        setPendingAttachments((prev) =>
          markAttachmentUploaded(prev, key, att.id)
        ),
      onError: (err) => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPendingAttachments((prev) => dropAttachment(prev, key));
        toast.error(
          (err as Error).message === "file-too-large"
            ? "File is too large (max 25 MB)"
            : "Couldn't upload the file"
        );
      }
    });
  };

  const handleChatFilesSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ""; // allow re-selecting the same file
    const remaining = MAX_CHAT_ATTACHMENTS - pendingAttachments.length;
    if (files.length > remaining)
      toast.error("You can attach up to 5 files per message");
    files.slice(0, Math.max(0, remaining)).forEach(uploadChatFile);
  };

  const handleSmsFileSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (pendingAttachments.length >= MAX_SMS_ATTACHMENTS) {
      toast.error("You can attach up to 1 image to a text message");
      return;
    }
    // Client-side gate only — the accept filter and these checks are
    // bypassable, so communication-service re-validates image type and size.
    // Match the server's carrier-reliable allowlist so a picked file can't pass
    // here and then 400 after upload.
    if (!SMS_ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error("Attach a JPEG, PNG, GIF, or WEBP image");
      return;
    }
    if (file.size === 0) {
      toast.error("That image file is empty");
      return;
    }
    if (file.size > MAX_SMS_ATTACHMENT_BYTES) {
      toast.error("Image is too large (max 5 MB)");
      return;
    }
    uploadChatFile(file);
  };

  const removePendingAttachment = (key: string) =>
    setPendingAttachments((prev) => {
      const item = prev.find((a) => a.key === key);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((a) => a.key !== key);
    });

  // Remove only the given staged items (revoking just their preview URLs), so an
  // image attached after a send started isn't discarded along with the sent one.
  const clearStagedAttachments = (items: PendingAttachment[]) => {
    const keys = new Set(items.map((a) => a.key));
    setPendingAttachments((prev) => {
      prev.forEach(
        (a) =>
          keys.has(a.key) && a.previewUrl && URL.revokeObjectURL(a.previewUrl)
      );
      return prev.filter((a) => !keys.has(a.key));
    });
  };

  const handleSend = () => {
    // Chat and SMS may be sent with only an attachment (empty text).
    if (isEmpty && !((isChat || isSms) && pendingAttachments.length > 0))
      return;

    const doc = editorRef.current?.getDocument();
    if (!doc) return;

    if (isChat) {
      if (applicationId !== undefined) doSendChat(doc);
      return;
    }
    if (isSms) {
      // The `hasUnresolvedLink` clause is a hard guard mirroring the email
      // path: never send an SMS that still carries an unresolved link chip,
      // regardless of button-disabled state.
      if (
        !phoneNumberId ||
        !toPhone ||
        isToOptedOut ||
        hasUnresolvedLink ||
        // Never send while the image is still uploading — otherwise an
        // attachment-only send would go out with no text and no image.
        isUploading ||
        isOverSmsLimit(doc.text)
      )
        return;
      doSendSms(doc);
    } else {
      // Never send an email that still carries an unresolved link chip,
      // mirroring the SMS guard above.
      if (!mailboxId || hasUnresolvedLink) return;
      if (isReply) {
        doSendEmail(doc);
        return;
      }
      if (!toEmail) return;
      // The pop-up has no subject field (design), so emails from it go out
      // subjectless without the warning that would otherwise fire every send.
      if (!subject.trim() && !isCompact) {
        setShowNoSubjectModal(true);
        return;
      }
      doSendEmail(doc);
    }
  };

  const applyTemplate = (plainText: string, html: string) => {
    editorRef.current?.setEditorValue(html);
    // Templates seed bare link tokens as UNRESOLVED link chips. When the
    // contact has exactly one unit, pre-select it so the user does not have
    // to disambiguate; otherwise leave them unresolved to block send.
    const editor = editorInstanceRef.current;
    if (editor && singleUnit) resolveUnresolvedLinkChips(editor, singleUnit);
    setIsEmpty(isTextEmpty(plainText));
    setSmsText(plainText);
    const doc = editorRef.current?.getDocument();
    setHasUnresolvedLink(docHasUnresolvedLinkChip(doc?.json));
    // setEditorValue doesn't emit an update, so persist the applied template
    // explicitly — it is unsent content just like typed text.
    persistDraft({ html: doc?.html ?? html, text: doc?.text ?? plainText });
  };

  const handleTemplateSelect = (plainText: string, html: string) => {
    if (!isEmpty) {
      setPendingTemplate({ plainText, html });
      return;
    }
    applyTemplate(plainText, html);
  };

  const smsLength = getSmsLength(smsText);
  const isOverLimit = isOverSmsLimit(smsText);

  const isSendDisabled =
    computeSendDisabled({
      isChat,
      isSms,
      isReply,
      isEmpty,
      isPending,
      isUploading,
      pendingCount: pendingAttachments.length,
      applicationId,
      phoneNumberId,
      toPhone,
      toEmail,
      mailboxId,
      isToOptedOut,
      hasUnresolvedLink
    }) ||
    (isSms && isOverLimit);

  return (
    <>
      <div className="flex flex-col">
        {/* The pop-up design (2152:16965) has no subject field — its email
            composer is a plain input card. */}
        {isEmail && !isReply && !isCompact ? (
          <div
            className={cx(
              isCompact ? "px-1 pb-3" : "border-b border-alpha-40 px-11 py-4"
            )}
          >
            <BaseInput
              id="email-subject"
              placeholder="Subject"
              value={subject}
              onChange={handleSubjectChange}
              size={InputSize.SM}
              autoComplete="off"
              className="[&>span]:!border-0 [&>span]:!shadow-none [&>span]:!h-auto"
              inputClassName="!p-0 border-0 shadow-none bg-transparent text-content-1000 font-medium text-sm placeholder:text-sm placeholder:font-medium placeholder:text-content-200 placeholder:not-italic"
            />
          </div>
        ) : null}

        <Show if={(isChat || isSms) && pendingAttachments.length > 0}>
          <div className={cx("pt-3", isCompact ? "px-1 pb-2" : "px-11")}>
            <PendingAttachmentPreview
              attachments={pendingAttachments}
              onRemove={removePendingAttachment}
            />
          </div>
        </Show>

        <div
          className={cx(
            "flex items-center",
            // The pop-up draws the design's bordered input card (node
            // 2039:32114); /inbox keeps its full-width row.
            isCompact
              ? "gap-2 rounded-lg border border-alpha-100 bg-white p-4 shadow-xs"
              : "gap-2 px-11 py-4"
          )}
        >
          {isChat || isSms ? (
            <>
              <input
                ref={chatFileInputRef}
                type="file"
                accept={isSms ? SMS_ALLOWED_IMAGE_TYPES.join(",") : undefined}
                multiple={isChat}
                className="hidden"
                onChange={
                  isSms ? handleSmsFileSelected : handleChatFilesSelected
                }
              />
              <button
                type="button"
                aria-label="Attach image"
                onClick={() => chatFileInputRef.current?.click()}
                disabled={
                  pendingAttachments.length >=
                  (isSms ? MAX_SMS_ATTACHMENTS : MAX_CHAT_ATTACHMENTS)
                }
                className="shrink-0 rounded-lg p-1 text-content-1000 transition-colors hover:bg-alpha-40 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <HugeiconsIcon icon={AttachmentIcon} size={20} />
              </button>
            </>
          ) : null}
          <Show if={!isCompact}>
            <InboxInsertVariableMenu
              variables={variables}
              units={units}
              values={variableValues}
              onInsertName={handleInsertName}
              onInsertLink={handleInsertLink}
              trigger={
                <button
                  type="button"
                  disabled={!contactId}
                  aria-label="Insert variable"
                  className="shrink-0 rounded-lg p-1 text-content-1000 transition-colors hover:bg-alpha-40 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <HugeiconsIcon icon={PlusSignIcon} size={24} />
                </button>
              }
            />
          </Show>

          <div className="flex min-w-0 flex-1 flex-col">
            {/* Chip node views render in portals under the editor, so this
                provider is what feeds them variables/units (and re-renders them
                when the queries resolve). */}
            <InboxVariableDataContext.Provider value={variableData}>
              <LiteTextEditor
                ref={editorRef}
                id={`${channel}-message-editor`}
                editable
                disableMentions
                extensions={variableExtension}
                placeholder="Type your message"
                containerClassName="flex-1 text-sm text-content-1000 min-h-0 font-medium"
                onChange={handleEditorChange}
                onEditorMount={(editor) => {
                  editorInstanceRef.current = editor;
                  setEditorReady(true);
                }}
              />
            </InboxVariableDataContext.Provider>
            {isSms ? (
              <div className="flex flex-wrap items-center gap-1.5 pt-1 text-xs">
                <span className="tabular-nums text-content-300">
                  {smsLength} characters
                </span>
                <Show if={isOverLimit}>
                  <SmsLengthNote />
                </Show>
                {/* The pop-up's input card is too narrow to seat the note
                    beside the editor (it squeezed the placeholder into it), so
                    there it sits under the text with the counter. */}
                <Show if={isCompact && isToOptedOut}>
                  <span className="font-medium text-danger-500">
                    Opted out of SMS
                  </span>
                </Show>
              </div>
            ) : null}
          </div>

          <div className="flex items-center gap-3">
            {isSms && isToOptedOut && !isCompact ? (
              <span className="text-xs font-medium text-danger-500">
                Opted out of SMS
              </span>
            ) : null}
            {/* The pop-up composer (2152:16965) has no template picker, so
                Templates stays /inbox-only for both channels. */}
            {isSms && !isCompact ? (
              <Suspense fallback={null}>
                <SmsTemplateSelect
                  contactId={contactId}
                  toNumber={toPhone || undefined}
                  onSelect={handleTemplateSelect}
                />
              </Suspense>
            ) : null}
            {/* Email templates belong to the email channel only. The old
                !isSms && !isChat form also matched the note composer, which
                surfaced them for partners without communication_email
                (CRM-1132). */}
            {isEmail && !isCompact && onTemplateSelect ? (
              <TemplatePickerButton onSelect={onTemplateSelect} />
            ) : null}
            <IconButton
              variant={IconButtonVariant.BASE}
              size={controlSize}
              HugeIcon={SentIcon}
              onClick={handleSend}
              disabled={isSendDisabled}
            />
          </div>
        </div>
      </div>

      {!isSms && !isChat ? (
        <NoSubjectWarningModal
          open={showNoSubjectModal}
          zIndex={embed?.modalZIndex}
          onClose={() => setShowNoSubjectModal(false)}
          onConfirm={() => {
            setShowNoSubjectModal(false);
            const doc = editorRef.current?.getDocument();
            if (doc) doSendEmail(doc);
          }}
        />
      ) : null}

      <ConfirmDialog
        opened={pendingTemplate !== null}
        onClose={() => setPendingTemplate(null)}
        onConfirm={() => {
          if (pendingTemplate)
            applyTemplate(pendingTemplate.plainText, pendingTemplate.html);
          setPendingTemplate(null);
        }}
        variant="warning"
        icon={{ icon: Alert02Icon, variant: "warning" }}
        title="Replace current message?"
        description="Applying this template will replace your current message."
        confirmButton={{ label: "Replace" }}
        cancelButton={{ label: "Cancel" }}
        zIndex={embed?.modalZIndex}
      />
    </>
  );
};
